import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import clsx from 'clsx';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';

type SelectProps<T extends FieldValues> = {
  field?: Path<T>;
  options?: RegisterOptions<T>;
  values: Array<{
    value: string;
    description?: string;
    icon?: {
      name: string;
      color?: string;
      tooltip?: string;
    };
    optionProps?: React.OptionHTMLAttributes<HTMLOptionElement>;
  }>;
  control?: Control<T>;
  disabled?: boolean;
  parentClassName?: string;
  noOptionSelectedText?: string;
  error?: string;
} & React.DetailedHTMLProps<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
>;

export function SelectInputT<T extends FieldValues>({
  field,
  options,
  values,
  control,
  style,
  parentClassName,
  noOptionSelectedText,
  error,
  ...restParams
}: SelectProps<T>) {
  let { disabled, ...registerOptions } = options || {};
  const { className, value, ...rest } = restParams;

  const registerObject =
    field && control ? control.register(field, registerOptions) : {};

  if (values.length === 0) {
    disabled = true;
  }

  const selectedOption = values.find((option) => option.value === value);

  let defaultStyle = [
    'h-auto',
    'block',
    'w-full',
    'rounded-md',
    'py-1.5',
    'pl-2.5',
    selectedOption?.icon?.name ? 'pr-14' : 'pr-8',
    'ring-1',
    'ring-inset',
    error ? 'ring-red-base' : 'ring-gray-200',
    'focus:ring-1',
    'focus:ring-inset',
    error ? 'focus:ring-red-base' : 'focus:ring-primary-base',
    'text-xs',
    'leading-5',
    'outline-none',
    'font-normal',
    'appearance-none',
    'overflow-ellipsis',
    'whitespace-nowrap',
    disabled ? 'bg-gray-100' : 'bg-white',
    disabled ? 'cursor-default' : 'cursor-pointer',
    disabled ? 'opacity-70' : '',
    disabled ? 'pointer-events-none' : '',
    disabled ? 'italic' : '',
    disabled ? 'text-gray-300' : '',
    typeof value !== 'undefined' && value === ''
      ? 'text-gray-300'
      : 'text-gray-600',
    'select-none',
  ];

  const arrowIconStyle = [
    'pointer-events-none',
    'absolute',
    'inset-y-0',
    'right-0',
    'flex',
    'items-center',
    'px-1.5',
    'text-gray-600',
    disabled ? 'text-gray-200' : '',
    error ? 'h-[calc(100%-16px)]' : 'h-full',
  ];

  // If className contains a leading-* class, remove leading-6 from inputDefaultStyle, otherwise the leading-6 is always overwritten the leading-* class that is coming from the parent component
  if (className?.includes('leading-')) {
    defaultStyle = defaultStyle.filter(
      (style) => !style.startsWith('leading-'),
    );
  }

  return (
    <div className={`flex flex-col relative ${parentClassName || ''}`}>
      <select
        value={values.length > 0 ? (value ?? undefined) : 'No results'}
        className={clsx([...defaultStyle, className])}
        {...registerObject}
        {...rest}>
        {values.length === 0 ? (
          <option value=''>No results</option>
        ) : (
          <>
            <option value='' disabled>
              {noOptionSelectedText || 'Select an option'}
            </option>
            {values.map(({ value, description, optionProps }) => (
              <option key={value} value={value} {...optionProps}>
                {description}
              </option>
            ))}
          </>
        )}
      </select>
      {!disabled && (
        <div className={clsx(arrowIconStyle)}>
          <MaterialIcon icon={'keyboard_arrow_down'} />
        </div>
      )}
      {selectedOption?.icon?.name && (
        <div className='absolute inset-y-0 right-6 flex items-center px-1.5'>
          <GeneralTooltip
            id={'flow.designer.sidebar.tab.title.general'}
            text={selectedOption?.icon?.tooltip || ''}
            placement='left'
            className='right-6 max-w-[140px] text-pico'>
            <MaterialIcon
              icon={selectedOption.icon.name}
              classes={[`text-${selectedOption.icon.color}`, 'text-xl']}
            />
          </GeneralTooltip>
        </div>
      )}
      {error && (
        <p className='text-red-base text-pico mt-0.5 h-[16px]'>{error}</p>
      )}
    </div>
  );
}

export default SelectInputT;
